import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import type { IProduct } from '@/types/product';

export type RecommendationHighlights = Record<number, string>;

export interface RecommendationResult {
  recommended: number[];
  skipped: number[];
  highlights: RecommendationHighlights;
}

/**
 * Keep only catalog ids that are currently selectable. Excluded / unknown ids are skipped so a
 * recommended badge never lands on a disabled card.
 */
export function applyRecommendations(
  productIds: number[],
  reasons: Record<string, string> | undefined,
  catalog: Array<Pick<IProduct, 'id'>>,
  excludedIds: number[]
): RecommendationResult {
  const catalogIds = new Set(catalog.map((product) => product.id));
  const excluded = new Set(excludedIds);
  const recommended: number[] = [];
  const skipped: number[] = [];
  const highlights: RecommendationHighlights = {};

  for (const raw of productIds) {
    const id = Number(raw);
    if (!Number.isFinite(id) || !catalogIds.has(id) || excluded.has(id)) {
      skipped.push(id);
      continue;
    }
    recommended.push(id);
    const reason = reasons?.[String(id)];
    highlights[id] = typeof reason === 'string' ? reason : '';
  }

  return { recommended, skipped, highlights };
}

export function resolveOpenProduct<T extends Pick<IProduct, 'id' | 'name'>>(
  productId: number,
  catalog: T[],
  excludedIds: number[]
): { ok: true; product: T } | { ok: false; error: string } {
  const product = catalog.find((item) => item.id === productId);
  if (!product) {
    return { ok: false, error: `Unknown product_id: ${productId}` };
  }
  if (excludedIds.includes(productId)) {
    return {
      ok: false,
      error: `Product ${productId} is not available (already in the cart or excluded by another product).`,
    };
  }
  return { ok: true, product };
}

export interface RecommendProductsDeps {
  getCatalog: () => IProduct[];
  getExcludedIds: () => number[];
  setHighlights: (highlights: RecommendationHighlights) => void;
  scrollTo: (productId: number) => void;
}

export function createRecommendProductsFunction(deps: RecommendProductsDeps): AutopilotFunctionDefinition {
  return {
    name: 'recommend_products',
    description:
      'Highlight products on the booking catalog grid as recommended. Pass ids from context.product_catalog. ' +
      'Ids that are missing, already in the cart, or excluded are skipped. Replaces any previous highlight set. ' +
      'Pass an empty list to clear. Optional reasons (keyed by product id) appear on the cards.',
    parameters: {
      type: 'object',
      properties: {
        product_ids: {
          type: 'array',
          items: { type: 'integer' },
          description: 'Product ids from context.product_catalog to highlight.',
        },
        reasons: {
          type: 'object',
          additionalProperties: { type: 'string' },
          description: 'Optional short reason per product id (string keys), shown on the card.',
        },
      },
      required: ['product_ids'],
    },
    handler: async (rawArgs) => {
      const args = (rawArgs ?? {}) as { product_ids?: number[]; reasons?: Record<string, string> };
      const ids = Array.isArray(args.product_ids) ? args.product_ids : [];
      const result = applyRecommendations(ids, args.reasons, deps.getCatalog(), deps.getExcludedIds());
      deps.setHighlights(result.highlights);
      if (result.recommended.length) deps.scrollTo(result.recommended[0]);
      return { success: true, recommended: result.recommended, skipped: result.skipped };
    },
  };
}

export interface OpenProductDeps {
  getCatalog: () => IProduct[];
  getExcludedIds: () => number[];
  open: (product: IProduct) => void;
}

export function createOpenProductFunction(deps: OpenProductDeps): AutopilotFunctionDefinition {
  return {
    name: 'open_product',
    description:
      'Open the configure modal for a product on the booking catalog (same as the user clicking the card). ' +
      'Does not add it to the cart — the user still confirms dates, budget and options. ' +
      'Fails if the product is unknown, already in the cart, or excluded.',
    parameters: {
      type: 'object',
      properties: {
        product_id: {
          type: 'integer',
          description: 'Product id from context.product_catalog to open.',
        },
      },
      required: ['product_id'],
    },
    handler: async (rawArgs) => {
      const args = (rawArgs ?? {}) as { product_id?: number };
      const productId = Number(args.product_id);
      const result = resolveOpenProduct(productId, deps.getCatalog(), deps.getExcludedIds());
      if (!result.ok) return { success: false, error: result.error };
      deps.open(result.product);
      return { success: true, product_id: result.product.id, name: result.product.name };
    },
  };
}
